import React, { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import * as dayjs from "dayjs";
import IntlMessages, { IntlGetText } from "util/IntlMessages";
import { Button, Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions 
} from "@material-ui/core";
import FormDateTimePicker from "components/Form/Controls/dateTimePicker";
import FormSelectAutoComplete from "components/Form/Controls/select-autocomplete";
import ButtonAsync from "components/Form/Controls/ButtonAsync";
import {
    callApi,
    getImpactTypeList
} from "api/apiList";
import { isRole } from "app/routes/psp/services/userService";
import {  FolderType, RoleEnum } from "constants/Enums";


const DialogPlanningImpact = ({open, handleRequestClose, prescriptionList, callPlanImpact }) => {  
    const [impactTypeList, setImpactTypeList] = useState([]);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);

    const defaultValues = {
        prescriptionId: "",
        impactTypeId: "",
        planningDate: null
    };
    const validationSchema = {
        prescriptionId: yup
            .object()
            .shape({ value: yup.string().required() })
            .required(<IntlMessages id="core.fieldRequired" />)
            .nullable(),
        impactTypeId: yup
            .object()
            .shape({ value: yup.string().required() })
            .required(<IntlMessages id="core.fieldRequired" />)
            .nullable(),
        planningDate: yup
            .date()
            .nullable(true)
            .typeError(IntlGetText("yup.InvalidDate"))
            .required(<IntlMessages id="core.fieldRequired" />)
    };

    const isNurseProcedure = isRole(RoleEnum.nurse_procedure);
    const methods = useForm({ defaultValues,  resolver: yupResolver(yup.object().shape(validationSchema)) });
    const { handleSubmit, setValue, errors } = methods;
    
    // const loadData = () => {
    //     setValue("impactTypeId", !prescription ? "" : getCboValue(impactTypeList, prescription.impactTypeId));
    //     setValue("planningDate", !prescription || !prescription.planningDate ? null : prescription.planningDate);
    // };

    useEffect(()=>{
        callApi(getImpactTypeList, 
            (data) => {
                if(isNurseProcedure){
                    data = data.filter( x => x.folderType == FolderType.inProcess);
                }
                setImpactTypeList(data)
            }
        );
    },[]);

    useEffect(() => {
        // Reset only when opening. If we unlock on close, the button can
        // accept another tap while MUI's close animation is still visible (common on tablets).
        if (open) {
            savingRef.current = false;
            setSaving(false);
        }
    }, [open]);

    // useEffect(()=>{
    //     if(open){
    //         setTimeout(loadData,150);
    //     }
    // },[open]);

    const handleSave = async (event) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setSaving(true);

      let didSubmit = false;
      await handleSubmit(async (data) => {
        didSubmit = true;
        const obj = {
          prescriptionId: Number(data.prescriptionId.value),
          planningDate: dayjs(data.planningDate).format("YYYY-MM-DDTHH:mm:ss"),
          impactTypeId: Number(data.impactTypeId.value)
        };
        const success = await callPlanImpact(obj);
        if (!success) {
          savingRef.current = false;
          setSaving(false);
        }
      })(event);

      if (!didSubmit) {
        savingRef.current = false;
        setSaving(false);
      }
    };

    const handleClose = () => {
      if (savingRef.current) return;
      handleRequestClose();
    };

    const interactionStyle = saving ? { pointerEvents: "none" } : undefined;

    return (
        <Dialog open={open} fullWidth={true} maxWidth="xs" 
            disableBackdropClick={true}
            disableEscapeKeyDown={saving}
            onClose={handleClose} >
            <DialogTitle>{<IntlMessages id="impact.PlanningImpactTitle" />}</DialogTitle>
            <DialogContent className="new-patient-dialog-content" style={interactionStyle}>
                <FormProvider {...methods}>
                    <form className="row" noValidate autoComplete="off">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12} lg={12}>
                                <FormSelectAutoComplete
                                name="impactTypeId"
                                label={<IntlMessages id="impact.ImpactType" />}
                                options={impactTypeList}
                                margin="normal"
                                required={true}
                                errorobj={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                                <FormSelectAutoComplete
                                name="prescriptionId"
                                label={<IntlMessages id="core.patient" />}
                                options={prescriptionList}
                                margin="normal"
                                required={true}
                                errorobj={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={12} lg={12}>
                                <FormDateTimePicker
                                    name="planningDate"
                                    label={IntlGetText("core.dateAndTime")}  
                                    format="YYYY-MM-DD hh:mm A"  
                                    disablePast={true}
                                    required={true} 
                                    errorobj={errors}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </FormProvider>
            </DialogContent>
            <DialogActions style={interactionStyle}>
                <Button type="button" onClick={handleClose} color="secondary" disabled={saving}>
                    {<IntlMessages id="core.cancel" />}
                </Button>
                <ButtonAsync type="button" onClick={handleSave} color="primary" disabled={saving}>
                    {<IntlMessages id="core.save" />}
                </ButtonAsync>
            </DialogActions>
        </Dialog>
    );
  };

  export default DialogPlanningImpact;
